import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { PlayerAccess } from '../../services';
import Accordion from '../shared/Accordion';
import Tooltip from '../shared/Tooltip';
import TableScrollTopBar from '../shared/TableScrollTopBar';
import DualCalendarDropdown from '../../../../shared/components/ui/DualCalendarDropdown';
import LoadingSpinner from '../../../../shared/components/ui/LoadingSpinner';
import { playerAccessService } from '../../services';

interface PlayerAccessTableProps {
  searchQuery: string;
  refreshTrigger?: number;
  onEdit?: (entity: PlayerAccess) => void;
}

type PlayerAccessKey = keyof PlayerAccess;

interface TableFilters {
  active: 'all' | 'true' | 'false';
  accessType: 'all' | string;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  updatedStartDate: Date | null;
  updatedEndDate: Date | null;
}

const PlayerAccessTable: React.FC<PlayerAccessTableProps> = ({
  searchQuery,
  refreshTrigger,
  onEdit
}) => {
  const [data, setData] = useState<PlayerAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<TableFilters>({
    active: 'all',
    accessType: 'all',
    createdStartDate: null,
    createdEndDate: null,
    updatedStartDate: null,
    updatedEndDate: null
  });
  const [activeFilterType, setActiveFilterType] = useState<'created' | 'updated' | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const createdCalendarButtonRef = useRef<HTMLButtonElement>(null);
  const updatedCalendarButtonRef = useRef<HTMLButtonElement>(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    recordsPerPage: 100,
    totalRecords: 0,
    totalPages: 0
  });

  const schema = [
    { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
    { name: 'player_name', type: 'varchar', nullable: false, width: 220, minWidth: 150, maxWidth: 280 },
    { name: 'player_details', type: 'varchar', nullable: true, width: 180, minWidth: 120, maxWidth: 220 },
    { name: 'organisation_name', type: 'varchar', nullable: false, width: 220, minWidth: 150, maxWidth: 280 },
    { name: 'access_type', type: 'varchar', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'granted_by', type: 'uuid', nullable: true, foreignKey: 'users.id', width: 250, minWidth: 200, maxWidth: 300 },
    { name: 'granted_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
    { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
  ];

  const calculateCalendarPosition = (buttonRef: React.RefObject<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      });
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const playerAccess = await playerAccessService.getAll();
      setData(playerAccess);
    } catch (err) {
      console.error('Error fetching player access:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  const filteredData = useMemo(() => {
    let filtered = data;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(query)
        )
      );
    }

    // Active filter
    if (filters.active !== 'all') {
      filtered = filtered.filter(item => 
        filters.active === 'true' ? item.active : !item.active
      );
    }

    // Access type filter
    if (filters.accessType !== 'all') {
      filtered = filtered.filter(item => item.access_type === filters.accessType);
    }

    // Date filters
    if (filters.createdStartDate && filters.createdEndDate) {
      filtered = filtered.filter(item => {
        const createdDate = new Date(item.created_at);
        return createdDate >= filters.createdStartDate! && createdDate <= filters.createdEndDate!;
      });
    }

    if (filters.updatedStartDate && filters.updatedEndDate) {
      filtered = filtered.filter(item => {
        const updatedDate = new Date(item.updated_at);
        return updatedDate >= filters.updatedStartDate! && updatedDate <= filters.updatedEndDate!;
      });
    }

    return filtered;
  }, [data, searchQuery, filters]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = (a as any)[sortConfig.key];
      const bValue = (b as any)[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortConfig.direction === 'asc' 
          ? (aValue === bValue ? 0 : (aValue ? 1 : -1))
          : (aValue === bValue ? 0 : (aValue ? -1 : 1));
      }

      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.recordsPerPage;
    return sortedData.slice(startIndex, startIndex + pagination.recordsPerPage);
  }, [sortedData, pagination.currentPage, pagination.recordsPerPage]);

  useEffect(() => {
    if (filteredData.length >= 0) {
      setPagination(prev => ({
        ...prev,
        totalRecords: filteredData.length,
        totalPages: Math.ceil(filteredData.length / prev.recordsPerPage),
        currentPage: 1
      }));
    }
  }, [filteredData]);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current && current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleRecordsPerPageChange = (recordsPerPage: number) => {
    setPagination(prev => ({
      ...prev,
      recordsPerPage,
      currentPage: 1,
      totalPages: Math.ceil(prev.totalRecords / recordsPerPage)
    }));
  };

  const formatTableData = (value: any, columnName: string, item?: PlayerAccess): string => {
    // Handle special columns first, before checking if value is null/undefined
    if (columnName === 'player_details') {
      // Format player details from the joined data
      if (!item) return '';
      const details = [];
      if (item.player_position) details.push(item.player_position);
      if (item.player_nationality) details.push(item.player_nationality);
      if (item.player_kit_number) details.push(`#${item.player_kit_number}`);
      return details.length > 0 ? details.join(' • ') : '';
    }
    
    // For other columns, check if value is null/undefined
    if (value === null || value === undefined) return '';
    
    if (columnName === 'granted_at' || columnName === 'created_at' || columnName === 'updated_at') {
      return new Date(value).toLocaleDateString();
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 overflow-visible">
      {/* Table Schema Accordion */}
      <Accordion
        title="Table Schema"
        defaultOpen={false}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-t border-b border-gray-200">
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Column</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Type</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Nullable</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Primary Key</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 bg-gray-100">Foreign Key</th>
              </tr>
            </thead>
            <tbody>
              {schema.map((column) => (
                <tr key={column.name} className="border-b border-gray-200">
                  <td className="px-3 py-2 text-sm text-gray-900">{column.name}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{column.type}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{column.nullable ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{column.primaryKey ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{column.foreignKey || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Accordion>

      {/* Table Data Accordion */}
      <Accordion
        title="Table Data"
        defaultOpen={true}
      >
        <div className="space-y-4 overflow-visible">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-lg overflow-visible">
            {/* Active Filter */}
            <select
              value={filters.active}
              onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value as 'all' | 'true' | 'false' }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Active: All</option>
              <option value="true">Active: Yes</option>
              <option value="false">Active: No</option>
            </select>

            {/* Access Type Filter */}
            <select
              value={filters.accessType}
              onChange={(e) => setFilters(prev => ({ ...prev, accessType: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Access Type: All</option>
              <option value="owner">Access Type: Owner</option>
              <option value="participant">Access Type: Participant</option>
              <option value="viewer">Access Type: Viewer</option>
            </select>

            {/* Date Filters */}
            <div className="flex items-center space-x-2 relative">
              <button
                ref={createdCalendarButtonRef}
                onClick={() => {
                  calculateCalendarPosition(createdCalendarButtonRef);
                  setActiveFilterType('created');
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  filters.createdStartDate || filters.createdEndDate
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Created: {filters.createdStartDate || filters.createdEndDate ? 'Selected' : 'Select'}
              </button>

              <button
                ref={updatedCalendarButtonRef}
                onClick={() => {
                  calculateCalendarPosition(updatedCalendarButtonRef);
                  setActiveFilterType('updated');
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  filters.updatedStartDate || filters.updatedEndDate
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Updated: {filters.updatedStartDate || filters.updatedEndDate ? 'Selected' : 'Select'}
              </button>

              {/* Calendar Dropdowns - Rendered via Portal */}
              {isCalendarOpen && createPortal(
                <div 
                  className="fixed z-[9999]"
                  style={{
                    top: `${calendarPosition.top}px`,
                    left: `${calendarPosition.left}px`
                  }}
                >
                  {activeFilterType === 'created' && (
                    <DualCalendarDropdown
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateRangeSelect={(startDate, endDate) => {
                        setFilters(prev => ({
                          ...prev,
                          createdStartDate: startDate,
                          createdEndDate: endDate
                        }));
                        setIsCalendarOpen(false);
                      }}
                      triggerRef={createdCalendarButtonRef}
                    />
                  )}
                  {activeFilterType === 'updated' && (
                    <DualCalendarDropdown
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateRangeSelect={(startDate, endDate) => {
                        setFilters(prev => ({
                          ...prev,
                          updatedStartDate: startDate,
                          updatedEndDate: endDate
                        }));
                        setIsCalendarOpen(false);
                      }}
                      triggerRef={updatedCalendarButtonRef}
                    />
                  )}
                </div>,
                document.body
              )}
            </div>

            {/* Clear All Filters */}
            <button
              onClick={() => setFilters({
                active: 'all',
                accessType: 'all',
                createdStartDate: null,
                createdEndDate: null,
                updatedStartDate: null,
                updatedEndDate: null
              })}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Clear Filters
            </button>
          </div>

          {/* Table */}
          <TableScrollTopBar contentClassName="overflow-y-visible">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 border-t border-b border-gray-200">
                <tr>
                  {schema.map((column) => (
                    <th
                      key={column.name}
                      className="px-3 py-3 text-left text-sm font-bold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors border-r border-gray-200 bg-gray-100"
                      onClick={() => handleSort(column.name)}
                      style={{ width: column.width }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.name}</span>
                        {sortConfig?.key === column.name && (
                          <span className="text-blue-500">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 w-20 bg-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={schema.length + 1} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <span className="text-4xl mb-2">📭</span>
                        <span className="text-lg font-medium">No data to show</span>
                        <span className="text-sm text-gray-400 mt-1">Try adjusting your filters or check back later</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                paginatedData.map((item) => (
                  <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                    {schema.map((column) => (
                      <td key={column.name} className="px-3 py-2 text-sm text-gray-900">
                        <Tooltip content={formatTableData(item[column.name as PlayerAccessKey], column.name, item)}>
                          <div className="truncate" style={{ width: column.width }}>
                            {formatTableData(item[column.name as PlayerAccessKey], column.name, item)}
                          </div>
                        </Tooltip>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => onEdit?.(item)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </TableScrollTopBar>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select
                value={pagination.recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                disabled={pagination.totalRecords === 0}
                className="px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={100}>100</option>
                <option value={1000}>1000</option>
              </select>
              <span className="text-sm text-gray-700">
                Showing {pagination.totalRecords === 0 ? 0 : (pagination.currentPage - 1) * pagination.recordsPerPage + 1} to{' '}
                {pagination.totalRecords === 0 ? 0 : Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of{' '}
                {pagination.totalRecords} records
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1 || pagination.totalRecords === 0}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  pagination.currentPage === 1 || pagination.totalRecords === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {pagination.totalRecords === 0 ? 0 : pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages || pagination.totalRecords === 0}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  pagination.currentPage === pagination.totalPages || pagination.totalRecords === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default PlayerAccessTable;
